import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Anchor, Compass, User, ScrollText, ArrowLeftRight, ArrowDown, ArrowUp, LogOut } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLiveEvents } from "@/lib/useLiveEvents";

/**
 * Guests Onboard — quick-glance panel for the crew.
 *
 * Lists every guest on an active charter with a photo, name, age, cabin,
 * and an Onboard / Ashore switch. Flipping the switch is a coordination
 * signal (not a security gate) so any dashboard role can toggle — including
 * crew members. Auto-refreshes every 20 s so the roster stays honest as
 * guests come and go through the day.
 *
 * Below the grid a "Ship's log" surfaces the timestamped, signed history
 * of every shore/return declaration — the backend now writes an immutable
 * `onboard_events` row on every flip, and this panel renders them so the
 * captain can audit who declared what and when.
 */
export default function GuestsOnboardPanel() {
  const { admin } = useAuth();
  const canSeePhotos = (admin?.role || "") !== "spa_ops";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pending, setPending] = useState(null); // guest id being toggled
  // Confirmation dialog — before any toggle, we open this to make the
  // crew acknowledge that the change is signed. `pendingToggle` holds
  // the row + the intended next state so the OK handler doesn't need
  // to re-derive them.
  const [pendingToggle, setPendingToggle] = useState(null);
  // Shore/return log — reverse-chron, capped by the backend (100 rows).
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(true);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    try {
      const { data } = await api.get("/admin/guests/onboard-list");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not load the guest list");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadLog = useCallback(async (silent = false) => {
    if (!silent) setLogLoading(true);
    try {
      const { data } = await api.get("/admin/guests/onboard-log", { params: { limit: 100 } });
      setLog(Array.isArray(data) ? data : []);
    } catch (e) {
      // Log loading failures are non-fatal — the switches keep working.
      if (!silent) toast.error(formatErr(e?.response?.data?.detail) || "Could not load the ship's log");
    } finally {
      setLogLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadLog();
    const id = setInterval(() => { load(true); loadLog(true); }, 20000);
    return () => clearInterval(id);
  }, [load, loadLog]);

  // Live sync — any operator on any device that flips a guest's onboard
  // switch fires a `guest.onboard_changed` WebSocket event, and every
  // Guests-Onboard panel refetches within the same tick so the fleet
  // agrees on the state without waiting for the 20 s poll.
  useLiveEvents(useCallback((ev) => {
    if (ev?.type === "guest.onboard_changed") { load(true); loadLog(true); }
    // Trip-management edits (guest add / edit / delete / disembark passing)
    // trigger a `charter.updated` event — refresh so the panel mirrors the
    // Trips & guests state without a manual reload.
    if (ev?.type === "charter.updated") load(true);
  }, [load, loadLog]));

  // Stage the toggle in a confirmation dialog rather than firing straight
  // through — this makes the "signed under your name" behaviour explicit.
  const requestToggle = (row) => {
    setPendingToggle({ row, next: !row.onboard });
  };

  const confirmToggle = async () => {
    if (!pendingToggle) return;
    const { row, next } = pendingToggle;
    setPendingToggle(null);
    setPending(row.id);
    // Optimistic — flip the local state first so the switch feels instant
    // on iPad-taps; roll back on error.
    setRows((r) => r.map((x) => (x.id === row.id ? { ...x, onboard: next } : x)));
    try {
      await api.patch(`/admin/guests/${row.id}/onboard`, { onboard: next });
      toast.success(`${row.name} · ${next ? "Onboard" : "Ashore"} · signed by ${admin?.name || admin?.email || "you"}`);
      loadLog(true);
    } catch (e) {
      setRows((r) => r.map((x) => (x.id === row.id ? { ...x, onboard: !next } : x)));
      toast.error(formatErr(e?.response?.data?.detail) || "Could not update — try again");
    } finally {
      setPending(null);
    }
  };

  // Final disembarkation — stamps stays[current].disembark_at so the guest
  // is permanently removed from the onboard roster AND their Trips tab
  // reflects the disembark date. Distinct from the temporary "ashore"
  // switch which only affects the coordination flag.
  const confirmFinalDisembark = async () => {
    if (!pendingToggle) return;
    const { row } = pendingToggle;
    setPendingToggle(null);
    setPending(row.id);
    try {
      await api.post(`/admin/guests/${row.id}/disembark-now`, {});
      toast.success(`${row.name} · Disembarked · signed by ${admin?.name || admin?.email || "you"}`);
      // The guest disappears from the onboard-list next fetch — refresh
      // both the roster and the ledger so the UI catches up immediately.
      await Promise.all([load(true), loadLog(true)]);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not disembark — try again");
    } finally {
      setPending(null);
    }
  };

  // Roll the counts up so the crew sees "6 onboard · 2 ashore · 8 total"
  // without having to count avatars themselves.
  const totals = useMemo(() => {
    const on = rows.filter((r) => r.onboard).length;
    return { on, off: rows.length - on, total: rows.length };
  }, [rows]);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h2 className="font-display text-3xl">Guests onboard</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Every guest currently on a live charter. Flip the switch when a guest goes ashore so the rest of the crew knows not to expect them for treatments.
          </p>
          {rows.length > 0 && (
            <div className="mt-3 flex items-center gap-4 text-xs" data-testid="guests-onboard-totals">
              <span className="flex items-center gap-1.5">
                <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" /> {totals.on} onboard
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-flex h-2 w-2 rounded-full bg-amber-500" /> {totals.off} ashore
              </span>
              <span className="text-muted-foreground">· {totals.total} total</span>
            </div>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => { load(true); loadLog(true); }} disabled={loading || refreshing} data-testid="guests-onboard-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="guests-onboard-empty">
          <Anchor className="h-6 w-6 mx-auto mb-3 opacity-60" strokeWidth={1.4} />
          No live charter right now — flip a trip to Active in the <span className="text-foreground font-medium">Trips &amp; guests</span> tab, or add guests to your current active trip. The roster refreshes automatically.
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="guests-onboard-list">
          {rows.map((r) => (
            <li
              key={r.id}
              className={`border ${r.onboard ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5 opacity-90"} p-4 flex items-center gap-4 transition-colors`}
              data-testid={`guest-row-${r.id}`}
            >
              {canSeePhotos && r.image_path ? (
                <img
                  src={fileUrl(r.image_path)}
                  alt={r.name}
                  loading="lazy"
                  decoding="async"
                  className="w-14 h-14 rounded-full object-cover flex-shrink-0 border border-border"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center flex-shrink-0 border border-border">
                  <User className="h-6 w-6 text-muted-foreground" strokeWidth={1.4} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg truncate" data-testid={`guest-name-${r.id}`}>{r.name}</div>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground flex-wrap">
                  {r.age != null && (
                    <span data-testid={`guest-age-${r.id}`}>Age {r.age}</span>
                  )}
                  <span className="flex items-center gap-1">
                    <Compass className="h-3 w-3" strokeWidth={1.5} />
                    {r.cabin_name}
                  </span>
                </div>
                <div className="mt-1">
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${r.onboard ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400" : "border-amber-500/50 text-amber-700 dark:text-amber-500"}`}
                    data-testid={`guest-status-${r.id}`}
                  >
                    {r.onboard ? "Onboard" : "Ashore"}
                  </Badge>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <Switch
                  checked={r.onboard}
                  onCheckedChange={() => requestToggle(r)}
                  disabled={pending === r.id}
                  data-testid={`guest-onboard-switch-${r.id}`}
                  aria-label={`Toggle onboard status for ${r.name}`}
                />
                <span className="text-[9px] tracking-widest uppercase text-muted-foreground">
                  {r.onboard ? "on" : "ashore"}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Ship's Log — styled as an aged captain's ledger. Deliberately
          set below the roster so the crew's primary action (the switch)
          stays at the top; the log is a review surface. */}
      <ShipLog log={log} loading={logLoading} />

      {/* Confirmation dialog — makes the "signed under your name" behaviour
          explicit before the switch actually flips. Cancel restores the
          switch to its previous position (we never optimistically flipped). */}
      <AlertDialog open={!!pendingToggle} onOpenChange={(o) => { if (!o) setPendingToggle(null); }}>
        <AlertDialogContent data-testid="onboard-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-accent" />
              {pendingToggle?.next
                ? `Bring ${pendingToggle?.row?.name} back onboard?`
                : `Mark ${pendingToggle?.row?.name} as ashore?`}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p className="text-sm">
                  This will change{" "}
                  <span className="font-medium text-foreground">{pendingToggle?.row?.name}</span>'s
                  status from{" "}
                  <Badge variant="outline" className={pendingToggle?.next ? "border-amber-500/50 text-amber-700 dark:text-amber-500" : "border-emerald-500/40 text-emerald-700 dark:text-emerald-400"}>
                    {pendingToggle?.next ? "Ashore" : "Onboard"}
                  </Badge>
                  {" → "}
                  <Badge variant="outline" className={pendingToggle?.next ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400" : "border-amber-500/50 text-amber-700 dark:text-amber-500"}>
                    {pendingToggle?.next ? "Onboard" : "Ashore"}
                  </Badge>
                </p>
                {pendingToggle?.next === false && (
                  <div className="text-xs bg-amber-500/5 border border-amber-500/30 p-3 space-y-1.5">
                    <div className="font-medium text-foreground flex items-center gap-1.5">
                      <ArrowLeftRight className="h-3 w-3 text-amber-600" />
                      Two ways to record this
                    </div>
                    <div className="text-muted-foreground leading-snug"><span className="text-foreground font-medium">Temporarily ashore</span> — short trip ashore, guest is expected back. Keeps them on the roster with an "Ashore" badge.</div>
                    <div className="text-muted-foreground leading-snug"><span className="text-foreground font-medium">Confirm disembarkation</span> — permanent departure. Stamps today's date on their stay, removes them from the onboard count, and shows the disembark in their Trip tab.</div>
                  </div>
                )}
                <div className="text-xs text-muted-foreground border-l-2 border-accent/60 pl-3 py-1 bg-accent/5">
                  <div className="overline text-primary/60 text-[10px] mb-1">This action will be logged under</div>
                  <div className="font-medium text-foreground">
                    {admin?.name || admin?.email || "your account"}
                  </div>
                  <div className="opacity-70">{admin?.role?.toUpperCase()}</div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* When declaring ashore, offer TWO destinations: the classic
              temporary "ashore" flip and a permanent "Disembark" that
              persists a disembark_at on the guest's active stay. Ordinary
              "bring back onboard" flows keep the single action. */}
          {pendingToggle?.next === false ? (
            <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
              <AlertDialogCancel data-testid="onboard-confirm-cancel" className="sm:mr-auto">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmToggle}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="onboard-confirm-ok"
              >
                <ArrowDown className="h-3.5 w-3.5 mr-1.5" />
                Temporarily ashore
              </AlertDialogAction>
              <AlertDialogAction
                onClick={confirmFinalDisembark}
                className="bg-rose-700 hover:bg-rose-800 text-white"
                data-testid="onboard-confirm-disembark"
              >
                <LogOut className="h-3.5 w-3.5 mr-1.5" />
                Confirm disembarkation
              </AlertDialogAction>
            </AlertDialogFooter>
          ) : (
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="onboard-confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmToggle}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="onboard-confirm-ok"
              >
                Yes, bring onboard
              </AlertDialogAction>
            </AlertDialogFooter>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


/**
 * Nautical captain's-ledger log surface. Deliberately visually distinct
 * from the roster above so the crew can tell at a glance whether they're
 * looking at "who's aboard right now" (the switches) or "who moved and
 * when" (this ledger).
 */
function ShipLog({ log, loading }) {
  return (
    <section className="mt-10 border border-primary/20 bg-[hsl(var(--muted)/0.4)] shadow-inner" data-testid="ship-log-section">
      {/* Ledger banner — parchment-y strip with the "SHIP'S LOG" title. */}
      <header className="flex items-center gap-3 border-b border-primary/20 px-5 py-3 bg-primary/5">
        <ScrollText className="h-5 w-5 text-primary/70" strokeWidth={1.5} />
        <div className="flex-1">
          <div className="font-display text-lg leading-tight">Ship's Log · Shore &amp; Return Ledger</div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-0.5">
            Every declared departure and return, signed by the crew member on watch
          </div>
        </div>
        <Badge variant="outline" className="text-[10px] border-primary/30 text-primary/70">
          {log.length} {log.length === 1 ? "entry" : "entries"}
        </Badge>
      </header>

      {loading ? (
        <div className="p-6 text-sm text-muted-foreground text-center">Loading log…</div>
      ) : log.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground italic" data-testid="ship-log-empty">
          The ledger is quiet — no shore or return declarations yet.
        </div>
      ) : (
        <ol className="divide-y divide-primary/15" data-testid="ship-log-list">
          {log.map((e) => (
            <ShipLogEntry key={e.id} entry={e} />
          ))}
        </ol>
      )}
    </section>
  );
}


function ShipLogEntry({ entry }) {
  // Format the timestamp in the ship's local timezone as best we can —
  // browsers already do this and the ledger looks more authentic when
  // times read like a physical logbook.
  const when = new Date(entry.at);
  const dateStr = when.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  const timeStr = when.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const wentOnboard = !!entry.onboard;
  // A "final" event is the permanent-disembark endpoint firing — visually
  // distinct from a temporary shore-leave so the captain can audit which
  // guests actually left the ship for good vs. just ran an errand.
  const isFinal = !!entry.final;

  const goingAshoreColor = isFinal
    ? "border-rose-500/50 bg-rose-500/10 text-rose-700 dark:text-rose-400"
    : "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400";
  const chipColor = wentOnboard
    ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
    : (isFinal ? "bg-rose-500/15 text-rose-700 dark:text-rose-400" : "bg-amber-500/15 text-amber-700 dark:text-amber-500");
  const chipLabel = wentOnboard
    ? "Returned onboard"
    : (isFinal ? "Disembarked" : "Went ashore");

  return (
    <li className="flex items-start gap-4 px-5 py-3.5 hover:bg-primary/5 transition-colors" data-testid={`ship-log-entry-${entry.id}`}>
      {/* Direction column — up-arrow returning onboard, down-arrow going ashore. */}
      <div className={`shrink-0 mt-0.5 w-8 h-8 rounded-full border flex items-center justify-center ${wentOnboard ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : goingAshoreColor}`}>
        {wentOnboard ? <ArrowUp className="h-4 w-4" strokeWidth={2} /> : (isFinal ? <LogOut className="h-4 w-4" strokeWidth={2} /> : <ArrowDown className="h-4 w-4" strokeWidth={2} />)}
      </div>

      {/* Main entry — bearing, guest, actor. Reads like a log line: */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-display text-base leading-tight" data-testid={`ship-log-guest-${entry.id}`}>
            {entry.guest_name || "Unknown guest"}
          </span>
          {entry.cabin_name && (
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-0.5">
              <Compass className="h-2.5 w-2.5" strokeWidth={1.5} />
              {entry.cabin_name}
            </span>
          )}
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-widest ${chipColor}`}
            data-testid={`ship-log-kind-${entry.id}`}
          >
            {chipLabel}
          </span>
          {isFinal && entry.disembark_port && (
            <span className="text-[10px] text-muted-foreground">at {entry.disembark_port}</span>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
          <span>signed by</span>
          <span className="font-medium text-foreground" data-testid={`ship-log-actor-${entry.id}`}>
            {entry.actor_name || entry.actor_email || "System"}
          </span>
          {entry.actor_role && (
            <Badge variant="outline" className="text-[9px] uppercase tracking-widest border-primary/20 text-primary/60">
              {entry.actor_role}
            </Badge>
          )}
        </div>
      </div>

      {/* Timestamp column — right-aligned, monospaced-feeling for the ledger vibe. */}
      <div className="shrink-0 text-right">
        <div className="text-xs font-medium text-foreground tabular-nums">{timeStr}</div>
        <div className="text-[10px] text-muted-foreground tabular-nums">{dateStr}</div>
      </div>
    </li>
  );
}
